/**
 * /select-tenant — сторінка вибору активного tenant'а після login.
 *
 * Логіка (за спецою 3.0.5 розд. 1.1):
 * - 0 tenants → redirect /no-tenants.
 * - 1 tenant  → автоматично виставляємо active_tenant_id і → / (навіть з
 *   ?reason=membership_lost: показувати warning немає сенсу, бо вибору
 *   немає; user усе одно йде у єдиний доступний).
 * - N tenants → показуємо UI вибору; якщо `?reason=membership_lost` —
 *   warning над списком + одразу зчищаємо query (щоб refresh не показав
 *   знову, інакше user «застрягає» з повідомленням про вже неактуальну
 *   подію).
 *
 * ч.1074: акаунт, куди зараз не пускає політика власника (`blocked_reason`
 * у /auth/my-tenants), автоматично не відкриваємо — показуємо причину і дії:
 * обовʼязковий двофакторний вхід — підтвердити вхід кодом або налаштувати
 * двофакторний вхід просто тут; довірені мережі — поточна адреса і що робити.
 * Сюди ж веде відмова посеред роботи (`?reason=access_blocked&tenant=<id>`).
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getMyTenants, logout as apiLogout, type MyTenantItem } from '@/api/auth';
import { tenantDisplayName } from '@/auth/tenantLabel';
import { enterTenant, leaveSession } from '@/auth/store';
import { nextAfterSelect } from '@/auth/nextAfterSelect';
import TwoFactorGate from '@/components/security/TwoFactorGate';

const BLOCK_LABEL: Record<string, string> = {
  TWO_FACTOR_REQUIRED: 'потрібен двофакторний вхід',
  IP_NOT_ALLOWED: 'вхід лише з довірених мереж',
};

function BlockedTenantPanel({ tenant, onUnblocked, onRecheck }: {
  tenant: MyTenantItem;
  onUnblocked: () => void;
  onRecheck: () => void;
}) {
  if (tenant.blocked_reason === 'IP_NOT_ALLOWED') {
    return (
      <div>
        <h3 style={{ marginTop: 0 }}>Вхід в акаунт «{tenantDisplayName(tenant)}» обмежено мережею</h3>
        <p style={{ fontSize: '0.9rem' }}>
          Власник акаунта дозволив працювати в ньому лише з довірених мереж
          (наприклад, з офісу або через VPN). Ваша поточна адреса:{' '}
          <b>{tenant.blocked_ip ?? 'невідома'}</b>.
        </p>
        <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
          Підключіться з дозволеної мережі або попросіть власника акаунта додати
          цю адресу: «Адміністрування акаунта» → «Безпека» → «Довірені мережі».
        </p>
        <button type="button" onClick={onRecheck}>перевірити знову</button>
      </div>
    );
  }
  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Акаунт «{tenantDisplayName(tenant)}» вимагає двофакторний вхід</h3>
      <TwoFactorGate onDone={onUnblocked} />
    </div>
  );
}

export default function SelectTenantPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const next = nextAfterSelect(location.state);
  const [params, setParams] = useSearchParams();

  // Зчитуємо `?reason=membership_lost` ОДИН РАЗ при mount і зберігаємо
  // у локальний state. Так refresh сторінки (F5) НЕ показує повідомлення
  // знову — воно з'являється тільки при першому переході з 403-handler'а.
  // Сам query-параметр чистимо нижче через setParams.
  const [showMembershipLost] = useState(() => params.get('reason') === 'membership_lost');
  const [blockedFrom] = useState(() => (
    params.get('reason') === 'access_blocked' ? params.get('tenant') : null));
  const [focusId, setFocusId] = useState<string | null>(blockedFrom);

  const tenants = useQuery<MyTenantItem[]>({
    queryKey: ['my-tenants'],
    queryFn: getMyTenants,
  });

  // Автоперенаправлення для 0/1 tenants (крім заблокованого політикою).
  useEffect(() => {
    if (!tenants.data) return;
    if (tenants.data.length === 0) {
      navigate('/no-tenants', { replace: true });
    } else if (tenants.data.length === 1 && !tenants.data[0]!.blocked_reason) {
      // 1 tenant — обираємо автоматично. Навіть при ?reason=membership_lost
      // показувати warning немає сенсу: вибору в user'а немає, він
      // одразу потрапляє у єдиний доступний tenant. Warning доречний
      // лише коли є кілька варіантів і user має зробити вибір.
      const onlyTenant = tenants.data[0]!;
      enterTenant(onlyTenant.id, next);
    }
  }, [tenants.data, navigate, next]);

  // Чистимо `?reason=...` із URL після першого рендеру,
  // щоб у history залишився чистий /select-tenant. Стан зафіксований у
  // useState — UI продовжує показуватись.
  useEffect(() => {
    if (params.get('reason') || params.get('tenant')) {
      const nextParams = new URLSearchParams(params);
      nextParams.delete('reason');
      nextParams.delete('tenant');
      setParams(nextParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pickTenant(t: MyTenantItem) {
    if (t.blocked_reason) {
      setFocusId(t.id);
      return;
    }
    enterTenant(t.id, next);
  }

  function onUnblocked(t: MyTenantItem) {
    // Сесію підтверджено кодом — перечитуємо список і заходимо.
    void queryClient.invalidateQueries({ queryKey: ['my-tenants'] });
    void queryClient.invalidateQueries({ queryKey: ['account-security'] });
    enterTenant(t.id, next);
  }

  async function onLogout() {
    try { await apiLogout(); } catch { /* вихід однаково */ }
    leaveSession();
  }

  if (tenants.isLoading) {
    return (
      <div className="login">
        <div className="card" style={{ width: 480 }}>
          Завантаження…
        </div>
      </div>
    );
  }

  if (tenants.isError) {
    return (
      <div className="login">
        <div className="card" style={{ width: 480 }}>
          <h2>Не вдалося завантажити список акаунтів</h2>
          <div className="error">{String(tenants.error)}</div>
        </div>
      </div>
    );
  }

  const list = tenants.data ?? [];
  const single = list.length === 1 ? list[0]! : null;

  // 0 або 1 доступний tenant — useEffect виконає редирект, рендер пустий.
  if (list.length === 0 || (single && !single.blocked_reason)) {
    return null;
  }

  const focused = single ?? list.find((t) => t.id === focusId && t.blocked_reason) ?? null;

  return (
    <div className="login">
      <div
        className="card"
        style={{ width: 'min(560px, 100%)', padding: '2rem', background: 'var(--bg-panel)' }}
      >
        {!single && <h2 style={{ marginTop: 0 }}>Виберіть клієнта для роботи</h2>}
        {showMembershipLost && !single && (
          <div
            style={{
              background: 'var(--primary-soft)',
              color: 'var(--text-secondary)',
              padding: '0.6rem 0.8rem',
              borderRadius: 'var(--radius-sm)',
              marginBottom: '1rem',
              fontSize: '0.875rem',
            }}
          >
Активуйте іншого клієнта зі списку нижче.
          </div>
        )}
        {focused && (
          <div style={{
            border: '1px solid var(--badge-amber-bd)', background: 'var(--badge-amber-bg)',
            borderRadius: 'var(--radius)', padding: '1rem', marginBottom: '1rem',
          }}>
            <BlockedTenantPanel tenant={focused}
              onUnblocked={() => onUnblocked(focused)}
              onRecheck={() => { void tenants.refetch(); }} />
          </div>
        )}
        {!single && (
          <div role="listbox" style={{ marginTop: '1rem' }}>
            {list.map((t) => (
              <button
                key={t.id}
                type="button"
                role="option"
                aria-selected={focused?.id === t.id}
                onClick={() => pickTenant(t)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '0.85rem 1rem',
                  marginBottom: '0.5rem',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  background: 'var(--bg-panel)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <span>
                  <span style={{ fontWeight: 700 }}>{tenantDisplayName(t)}</span>
                  {/* ч.1075: власний ярлик — справжня назва поруч дрібно. */}
                  {t.label && (
                    <span style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                      {t.name}
                    </span>
                  )}
                </span>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', textAlign: 'right' }}>
                  {t.display_role}
                  {t.blocked_reason && (
                    <span style={{ display: 'block', color: 'var(--badge-amber-fg)' }}>
                      🔒 {BLOCK_LABEL[t.blocked_reason] ?? 'вхід обмежено'}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        )}
        <div style={{ marginTop: '0.75rem', textAlign: 'right' }}>
          <button type="button" onClick={() => { void onLogout(); }}>вийти</button>
        </div>
      </div>
    </div>
  );
}
